<template>
  <div class="real-time-trace">
    <div class="trace-header">
      <span class="trace-title">{{ $t('map.vehicleTrace') }}</span>
      <span class="trace-sub">{{ activeNodeName }}</span>
    </div>

    <div v-if="selectedVehicle" class="vehicle-card">
      <div class="vehicle-card-header">
        <div class="vehicle-card-name">
          <span class="vehicle-icon">V</span>
          {{ selectedVehicle.name }}
        </div>
        <span class="vehicle-status" :class="selectedVehicle.status">
          <span class="status-dot" :class="'status-' + (selectedVehicle.status === 'moving' ? 'normal' : selectedVehicle.status)"></span>
          {{ statusMap[selectedVehicle.status] }}
        </span>
      </div>
      <div class="vehicle-card-info">
        {{ $t('map.currentTemperature') }}: <strong>{{ selectedVehicle.temp }}°C</strong> &nbsp;|&nbsp; {{ $t('map.latitude') }}: {{ selectedVehicle.lat.toFixed(4) }}, {{ $t('map.longitude') }}: {{ selectedVehicle.lng.toFixed(4) }}
      </div>
    </div>

    <div class="metrics-row">
      <div class="metric-item">
        <div class="metric-value">{{ trajectoryMetrics.avgSpeed }}</div>
        <div class="metric-label">{{ $t('map.averageSpeed') }} km/h</div>
      </div>
      <div class="metric-item">
        <div class="metric-value">{{ trajectoryMetrics.maxTemp }}</div>
        <div class="metric-label">{{ $t('map.maxTemperature') }} °C</div>
      </div>
      <div class="metric-item">
        <div class="metric-value">{{ trajectoryMetrics.minTemp }}</div>
        <div class="metric-label">{{ $t('map.minTemperature') }} °C</div>
      </div>
      <div class="metric-item">
        <div class="metric-value">{{ trajectoryMetrics.points }}</div>
        <div class="metric-label">{{ $t('map.tracePoints') }}</div>
      </div>
    </div>

    <div class="timeline-section">
      <div class="section-label">{{ $t('map.drivingTimeline') }}</div>
      <div class="timeline">
        <div
          v-for="(point, idx) in trajectoryTimeline"
          :key="idx"
          class="timeline-item"
          :class="{ 'is-alert': point.alert }"
        >
          <span class="timeline-time">{{ point.time }}</span>
          <span class="timeline-temp">{{ point.temp }}°C</span>
        </div>
      </div>
    </div>

    <div class="trend-section">
      <div class="section-label">{{ $t('map.temperatureTrend') }}</div>
      <div class="trend-bars">
        <div
          v-for="(point, idx) in trajectoryTimeline"
          :key="idx"
          class="trend-bar-col"
        >
          <div class="trend-bar" :class="point.alert ? 'bar-danger' : 'bar-normal'" :style="{ height: trendBarHeight(point.temp) }"></div>
          <span class="trend-bar-label">{{ point.label }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { gpsInfo } from '@/api/api.js'
import bus from '@/utils/bus.js'

export default {
  name: 'RealTimeTrace',
  data () {
    return {
      activeNodeName: '',
      selectedVehicle: null,
      statusMap: { moving: this.$t('map.moving'), warning: this.$t('map.warning'), offline: this.$t('map.offline'), stopped: this.$t('map.stopped') },
      gpsData: [],
      updateTimer: null
    }
  },
  computed: {
    trajectoryMetrics () {
      if (!this.selectedVehicle || !this.gpsData.length) {
        return { avgSpeed: '0', maxTemp: '0', minTemp: '0', points: 0 }
      }
      const points = this.gpsData
      const avgSpeed = (points.reduce((s, p) => s + (p.speed || 0), 0) / points.length).toFixed(1)
      const temps = points.map(p => p.temp || 0).filter(t => t !== 0)
      return {
        avgSpeed,
        maxTemp: temps.length ? Math.max(...temps).toFixed(1) : '0',
        minTemp: temps.length ? Math.min(...temps).toFixed(1) : '0',
        points: points.length
      }
    },
    trajectoryTimeline () {
      if (!this.gpsData.length) return []
      return this.gpsData.map((p, i) => ({
        time: p.gpsTime ? this.formatTime(p.gpsTime) : ('T' + (i + 1)),
        temp: p.temp || 0,
        label: 'T' + (i + 1),
        alert: p.temp && p.temp > -10
      }))
    }
  },
  mounted () {
    bus.$on('sceneRealTimeTrace', ({ id, marker }) => {
      this.activeNodeName = marker ? marker.name : ''
      this.loadGPSData(id)
    })

    this.updateTimer = setInterval(() => {
      if (this.selectedVehicle) {
        this.loadGPSData(this.selectedVehicle.id)
      }
    }, 5000)
  },
  beforeDestroy () {
    bus.$off('sceneRealTimeTrace')
    if (this.updateTimer) {
      clearInterval(this.updateTimer)
    }
  },
  methods: {
    async loadGPSData (id) {
      const res = await gpsInfo({ carrierId: id })
      if (res.result) {
        this.gpsData = res.result
        if (res.result.length > 0) {
          const latest = res.result[0]
          this.selectedVehicle = {
            id: id,
            name: latest.carrierName || '',
            lat: latest.latitude || 0,
            lng: latest.longitude || 0,
            temp: latest.temp || 0,
            speed: latest.speed || 0,
            status: latest.temp && latest.temp > -10 ? 'warning' : 'moving'
          }
        }
      }
    },
    formatTime (timestamp) {
      if (!timestamp) return '--'
      const date = new Date(timestamp)
      const h = String(date.getHours()).padStart(2, '0')
      const m = String(date.getMinutes()).padStart(2, '0')
      return `${h}:${m}`
    },
    trendBarHeight (temp) {
      const min = -25
      const max = 5
      const pct = Math.max(5, Math.min(100, ((temp - min) / (max - min)) * 100))
      return pct + '%'
    }
  }
}
</script>

<style scoped lang="scss">
$bg-white: #ffffff;
$bg-gray-50: #f9fafb;
$bg-gray-100: #f3f4f6;
$border-color: #e5e7eb;
$text-900: #111827;
$text-700: #374151;
$text-500: #6b7280;
$text-400: #9ca3af;
$accent: #3b82f6;
$success: #10b981;
$warning: #f59e0b;
$danger: #ef4444;
$info: #60a5fa;
$offline: #9ca3af;
$radius: 8px;
$radius-lg: 12px;
$radius-full: 999px;

.real-time-trace {
  background: $bg-white;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 0;
  box-sizing: border-box;
}

.real-time-trace::-webkit-scrollbar {
  width: 4px;
}

.real-time-trace::-webkit-scrollbar-track {
  background: transparent;
}

.real-time-trace::-webkit-scrollbar-thumb {
  background: $bg-gray-100;
  border-radius: 999px;
}

.trace-header {
  padding: 16px;
  border-bottom: 1px solid $border-color;
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.trace-title {
  font-size: 14px;
  font-weight: 600;
  color: $text-900;
}

.trace-sub {
  font-size: 12px;
  color: $text-400;
}

.vehicle-card {
  background: $bg-gray-50;
  border-radius: $radius-lg;
  padding: 16px;
  border: 1px solid $border-color;
  margin: 16px;
}

.vehicle-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.vehicle-card-name {
  font-size: 15px;
  font-weight: 600;
  color: $text-900;
  display: flex;
  align-items: center;
  gap: 8px;
}

.vehicle-icon {
  width: 26px;
  height: 26px;
  background: linear-gradient(135deg, $accent, darken($accent, 15%));
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
}

.vehicle-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  padding: 3px 8px;
  border-radius: $radius-full;
  font-weight: 500;

  &.moving { background: rgba(16,185,129,0.1); color: $success; }
  &.warning { background: rgba(245,158,11,0.1); color: $warning; }
  &.offline { background: rgba(156,163,175,0.1); color: $offline; }
  &.stopped { background: rgba(96,165,250,0.1); color: $info; }
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;

  &.status-normal { background: $success; }
  &.status-warning { background: $warning; }
  &.status-danger { background: $danger; }
  &.status-offline { background: $offline; }
}

.vehicle-card-info {
  font-size: 12px;
  color: $text-500;

  strong { color: $text-900; font-family: 'JetBrains Mono', monospace; }
}

.metrics-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  padding: 0 16px 16px;
}

.metric-item {
  background: $bg-gray-50;
  border: 1px solid $border-color;
  border-radius: $radius;
  padding: 12px;
  text-align: center;
}

.metric-value {
  font-size: 18px;
  font-weight: 700;
  color: $text-900;
  font-family: 'JetBrains Mono', monospace;
}

.metric-label {
  font-size: 11px;
  color: $text-400;
  margin-top: 4px;
}

.section-label {
  font-size: 12px;
  font-weight: 600;
  color: $text-500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 12px;
  padding-left: 16px;
}

.timeline-section {
  padding-bottom: 16px;
}

.timeline {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0 16px;
}

.timeline-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: $bg-gray-50;
  border-radius: $radius;
  border: 1px solid $border-color;
  transition: all 0.2s;

  &.is-alert {
    background: rgba(239,68,68,0.05);
    border-color: rgba(239,68,68,0.2);
  }
}

.timeline-time {
  font-size: 11px;
  color: $text-400;
  font-family: 'JetBrains Mono', monospace;
}

.timeline-temp {
  font-size: 12px;
  font-weight: 500;
  color: $text-700;
  font-family: 'JetBrains Mono', monospace;

  .is-alert & { color: $danger; }
}

.trend-section {
  padding: 0 0 16px;
}

.trend-bars {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  padding: 0 16px;
  height: 120px;
  background: $bg-gray-50;
  border-radius: $radius;
  border: 1px solid $border-color;
  padding: 12px 16px;
  box-sizing: border-box;
}

.trend-bar-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  justify-content: flex-end;
  gap: 6px;
}

.trend-bar {
  width: 100%;
  border-radius: 4px 4px 0 0;
  transition: height 0.3s ease;
  min-height: 5px;

  &.bar-normal { background: linear-gradient(to top, $accent, lighten($accent, 10%)); }
  &.bar-danger { background: linear-gradient(to top, $danger, lighten($danger, 10%)); }
}

.trend-bar-label {
  font-size: 10px;
  color: $text-400;
}
</style>
